import { CompanySettings } from '../types/company';
import mitt from 'mitt';

type Events = {
  'company:ready': void;
  'tz:changed': string;
};

export type DayReport = {
  transaction_sum: number;
  // ...add other fields as needed
};

export class CompanyRegistry {
  private settings: CompanySettings | null = null;
  private dayReports: Map<string, DayReport> = new Map();

  // internal readiness / event emitter
  private emitter = mitt<Events>();
  private readyPromise: Promise<void> | null = null;
  private tzVersion = 0;

  /**
   * Updates the company settings with new data (legacy API).
   * Use hydrate(...) when settings are fetched to trigger events.
   * @param settings The new company settings to store.
   */
  public updateSettings(settings: CompanySettings): void {
    this.settings = settings;
    // Keep backward compatibility: emit ready if updateSettings is used directly
    this.emitReadyIfNeeded();
  }

  private emitReadyIfNeeded(): void {
    // If readyPromise exists, resolve it by emitting company:ready
    if (this.readyPromise) {
      // emit company:ready so awaiting callers are unblocked
      this.emitter.emit('company:ready');
    }
  }

  /**
   * Hydrate the registry with fetched settings. This is the preferred method to call
   * when company settings arrive from the backend because it emits readiness and
   * timezone change events.
   */
  public hydrate(newSettings: CompanySettings): void {
    const prevTz = this.settings?.timezone_name;
    this.settings = newSettings;
    const newTz = newSettings?.timezone_name;
    if (prevTz !== newTz) {
      this.tzVersion++;
      if (newTz) {
        this.emitter.emit('tz:changed', newTz);
      }
    }
    this.emitter.emit('company:ready');
  }

  /**
   * Retrieves the current company settings.
   * @returns The current company settings or null if not set.
   */
  public getSettings(): CompanySettings | null {
    return this.settings;
  }

  /**
   * Clears the current company settings.
   */
  public clearSettings(): void {
    this.settings = null;
    // increment tzVersion to indicate a change (no tz)
    this.tzVersion++;
    this.emitter.emit('tz:changed', 'UTC');
  }

  /**
   * Merges a day report for a specific date.
   * @param date The date string (YYYY-MM-DD)
   * @param payload The day report data
   */
  public mergeDayReport(date: string, payload: DayReport): void {
    this.dayReports.set(date, { ...payload });
  }

  /**
   * Retrieves the day report for a specific date.
   * @param date The date string (YYYY-MM-DD)
   * @returns The day report or null if not set.
   */
  public getDayReport(date: string): DayReport | null {
    const data = this.dayReports.get(date);
    return data ? { ...data } : null;
  }

  /**
   * Retrieves the salon's timezone from the settings.
   * @returns The IANA timezone name or 'UTC' if not set.
   */
  public getTimezone(): string {
    return this.settings?.timezone_name || 'UTC';
  }

  /**
   * Retrieves the currency short title from the company settings.
   * @returns The currency short title (e.g. "BYN", "Kč") or "" as fallback.
   */
  public getCurrencySymbol(): string {
    return this.settings?.currency_short_title || '';
  }

  /**
   * Returns a monotonic version counter for the timezone. Incremented when tz changes.
   */
  public getTimezoneVersion(): number {
    return this.tzVersion;
  }

  /**
   * Returns a promise that resolves once the company settings are available.
   * If settings are already present it resolves immediately.
   */
  public async ready(): Promise<void> {
    if (this.settings) return;
    if (this.readyPromise) return this.readyPromise;
    this.readyPromise = new Promise<void>((resolve) => {
      const unsub = this.onReady(() => {
        unsub();
        resolve();
      });
    });
    return this.readyPromise;
  }

  /**
   * Subscribe to the 'company:ready' event. Returns an unsubscribe function.
   */
  public onReady(fn: () => void): () => void {
    this.emitter.on('company:ready', fn);
    return () => this.emitter.off('company:ready', fn);
  }

  /**
   * Subscribe to timezone changes. Returns an unsubscribe function.
   */
  public onTimezoneChanged(fn: (tz: string) => void): () => void {
    this.emitter.on('tz:changed', fn);
    return () => this.emitter.off('tz:changed', fn);
  }
}
